'use client';

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { cn } from '@/shared/utils/formatting';

export type TotpStatus = '' | 'err' | 'ok';

interface TotpCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** `err` → red underline + shake; `ok` → green underline and digits. */
  status?: TotpStatus;
  size?: 'md' | 'lg';
  autoFocus?: boolean;
  label?: string;
}

export const TOTP_LENGTH = 6;

/**
 * Six underscore slots (design handoff two_factor_auth): 64px tall, 34px
 * digits, 2px underline; `lg` is the sign-in challenge at 76px / 42px.
 */
export function TotpCodeInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  status = '',
  size = 'md',
  autoFocus = false,
  label = 'Authenticator code',
}: TotpCodeInputProps) {
  const slot = cn(
    'w-full border-b-2 border-white/[0.16] text-text-primary font-semibold font-sans transition-colors',
    size === 'lg' ? 'h-[76px] text-[42px]' : 'h-16 text-[34px]',
    status === 'err' && 'border-red-primary text-text-primary',
    status === 'ok' && 'border-success text-success'
  );

  return (
    <InputOTP
      maxLength={TOTP_LENGTH}
      value={value}
      onChange={onChange}
      {...(onComplete ? { onComplete } : {})}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={label}
      autoComplete="one-time-code"
      containerClassName={cn(
        'ph-no-capture w-full pt-1.5 pb-0.5',
        status === 'err' && 'animate-[cp-shake_0.4s_ease]'
      )}
    >
      <InputOTPGroup
        className={cn('grid w-full', size === 'lg' ? 'gap-4' : 'gap-3', 'grid-cols-6')}
      >
        {Array.from({ length: TOTP_LENGTH }, (_, i) => (
          <InputOTPSlot key={i} index={i} aria-invalid={status === 'err'} className={slot} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
